import React, { useEffect } from "react";
import "./style.scss";
import "../../../style/feature.scss";
import "../../../style/typography.scss";
import "../../../style/block.scss";
import "../../../style/buttons.scss";
import "../../../style/forms.scss";
import { SubmitHandler, useForm } from "react-hook-form";
import { LOGIN_PAGE, REGISTRATION_SUCCESS_PAGE } from "../../routes";
import { useNavigate } from "react-router";
import useApiRequest from "../../../utils/hooks/useApiRequest";
import { CREATE_NEW_USER_URL } from "../../api/apiRoutes";
import { WebUser } from "../../models/WebUser";
import useFacility from "../../../utils/hooks/useFacility";
import { useDispatch, useSelector } from "react-redux";
import { State } from "../../store";
import { clearErrors } from "../../store/alerts/alertsActions";
import EADivider from "../shared/EADivider";

type Inputs = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  repeatPassword: string;
  phone: string;
  dobDay: string;
  dobMonth: string;
  dobYear: string;
  allowMailing: boolean;
  acceptStatute: boolean;
  acceptRodo: boolean;
  acceptAduls: boolean;
};

const Registration: React.FC = () => {
  const navigate = useNavigate();
  const { post } = useApiRequest();
  const dispatch = useDispatch();
  const facilityIds = useFacility();
  const {
    register,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<Inputs>({
    defaultValues: {
      acceptStatute: false,
      allowMailing: false,
      acceptRodo: false,
      acceptAduls: false,
      dobDay: "",
      dobMonth: "",
      dobYear: "1999",
    },
  });

  const loading = useSelector((state: State) => state.auth.loading);

  const handleRegistration: SubmitHandler<Inputs> = async (data) => {
    // Format: yyyy-MM-dd (np. "1990-07-15")
    const birthDay = `${data.dobYear}-${data.dobMonth}-${data.dobDay}`;

    const webUser: WebUser = {
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      password: data.password,
      phone: data.phone,
      allowMailing: data.allowMailing,
      birthDay: birthDay,
    } as WebUser;
    const { isError } = await post(CREATE_NEW_USER_URL(facilityIds), webUser);
    if (!isError) {
      navigate(REGISTRATION_SUCCESS_PAGE);
    }
  };
  const alertLogin = useSelector((state: State) => state.alerts.errors);

  useEffect(() => {
    dispatch(clearErrors());
    //eslint-disable-next-line
  }, []);

  return (
    <div className="wrap">
      <div className="blob b1"></div>
      <div className="blob b2"></div>
      <div className="wave"></div>

      <section className="card">
        <div className="header">
          <div className="badge" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <path
                d="M12 2C12 2 5 10 5 14.5C5 18.09 8.134 21 12 21C15.866 21 19 18.09 19 14.5C19 10 12 2 12 2Z"
                stroke="#3aa5f8"
                strokeWidth="1.6"
              />
              <path
                d="M9 14.8c0 1.77 1.79 3.2 3 3.2 1.21 0 3-1.43 3-3.2"
                stroke="#3aa5f8"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <div className="title">
            <h1>Klub Przyjaciół Term Uniejów — Rejestracja</h1>
            <p>Dołącz do newslettera Klubu Przyjaciół Term Uniejów. Wszystkie pola są wymagane.</p>
          </div>
        </div>

        <form id="signup" onSubmit={handleSubmit(handleRegistration)} noValidate>
          <div>
            <h3 style={{ margin: "0 0 4px 0", fontSize: "14px", fontWeight: 600, color: "var(--muted)" }}>
              Dane osobowe
            </h3>
          </div>

          <div className="grid two">
            <div>
              <label htmlFor="firstName">Imię</label>
              <div className="field">
                <svg className="icon" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 12a5 5 0 1 0-5-5 5 5 0 0 0 5 5Zm0 2c-4 0-8 2-8 5v1h16v-1c0-3-4-5-8-5Z"
                    stroke="#6aaee6"
                    strokeWidth="1.6"
                  />
                </svg>
                <input
                  id="firstName"
                  className="input"
                  type="text"
                  autoComplete="given-name"
                  {...register("firstName", {
                    required: "Imię jest wymagane",
                    minLength: { value: 2, message: "Imię musi mieć minimum 2 znaki" },
                  })}
                />
              </div>
              {errors.firstName && (
                <div className="error" style={{ display: "block" }}>
                  {errors.firstName.message}
                </div>
              )}
            </div>
            <div>
              <label htmlFor="lastName">Nazwisko</label>
              <div className="field">
                <svg className="icon" viewBox="0 0 24 24" fill="none">
                  <path d="M12 12a5 5 0 1 0-5-5 5 5 0 0 0 5 5Z" stroke="#6aaee6" strokeWidth="1.6" />
                  <path d="M4 19c1.5-3 5-5 8-5s6.5 2 8 5" stroke="#6aaee6" strokeWidth="1.6" />
                </svg>
                <input
                  id="lastName"
                  className="input"
                  type="text"
                  autoComplete="family-name"
                  {...register("lastName", {
                    required: "Nazwisko jest wymagane",
                    minLength: { value: 2, message: "Nazwisko musi mieć minimum 2 znaki" },
                  })}
                />
              </div>
              {errors.lastName && (
                <div className="error" style={{ display: "block" }}>
                  {errors.lastName.message}
                </div>
              )}
            </div>
          </div>

          <div className="grid two">
            <div>
              <label htmlFor="email">Adres e-mail</label>
              <div className="field">
                <svg className="icon" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M3 7l9 6 9-6M5 6h14a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Z"
                    stroke="#6aaee6"
                    strokeWidth="1.6"
                  />
                </svg>
                <input
                  id="email"
                  className="input"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  {...register("email", {
                    required: "E-mail jest wymagany",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Nieprawidłowy adres e-mail",
                    },
                  })}
                />
              </div>
              {errors.email && (
                <div className="error" style={{ display: "block" }}>
                  {errors.email.message}
                </div>
              )}
            </div>
            <div>
              <label htmlFor="phone">Numer telefonu</label>
              <div className="field">
                <svg className="icon" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M7 3h10a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Z"
                    stroke="#6aaee6"
                    strokeWidth="1.6"
                  />
                  <path d="M8 18h8" stroke="#6aaee6" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
                <input
                  id="phone"
                  className="input"
                  type="tel"
                  autoComplete="tel"
                  inputMode="tel"
                  placeholder="+48 600 000 000"
                  {...register("phone", {
                    required: "Numer telefonu jest wymagany",
                    pattern: {
                      value: /^[+]?\d[\d\s-]{6,}$/,
                      message: "Nieprawidłowy numer telefonu",
                    },
                  })}
                />
              </div>
              {errors.phone && (
                <div className="error" style={{ display: "block" }}>
                  {errors.phone.message}
                </div>
              )}
            </div>
          </div>

          <div className="grid two">
            <div>
              <label htmlFor="password">Hasło</label>
              <div className="field">
                <svg className="icon" viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="10" width="18" height="10" rx="2" stroke="#6aaee6" strokeWidth="1.6" />
                  <path d="M8 10V7a4 4 0 0 1 8 0v3" stroke="#6aaee6" strokeWidth="1.6" />
                </svg>
                <input
                  id="password"
                  className="input"
                  type="password"
                  aria-describedby="pwdHint"
                  {...register("password", {
                    required: "Hasło jest wymagane",
                    minLength: { value: 12, message: "Hasło musi mieć minimum 8 znaków" },
                  })}
                />
              </div>
              <small id="pwdHint" className="muted">
                Minimum 12 znaków.
              </small>
              {errors.password && (
                <div className="error" style={{ display: "block" }}>
                  {errors.password.message}
                </div>
              )}
            </div>

            <div>
              <label htmlFor="repeatPassword">Powtórz hasło</label>
              <div className="field">
                <svg className="icon" viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="10" width="18" height="10" rx="2" stroke="#6aaee6" strokeWidth="1.6" />
                  <path d="M8 10V7a4 4 0 0 1 8 0v3" stroke="#6aaee6" strokeWidth="1.6" />
                </svg>
                <input
                  id="repeatPassword"
                  className="input"
                  type="password"
                  {...register("repeatPassword", {
                    required: "Powtórz hasło",
                    validate: (value) => value === watch("password") || "Hasła muszą być identyczne",
                  })}
                />
              </div>
              {errors.repeatPassword && (
                <div className="error" style={{ display: "block" }}>
                  {errors.repeatPassword.message}
                </div>
              )}
            </div>
          </div>

          <EADivider />

          <div>
            <h3 style={{ margin: "0 0 8px 0", fontSize: "14px", fontWeight: 600, color: "var(--muted)" }}>
              Data urodzenia
            </h3>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr ", gap: "16px" }}>
            <div>
              <label htmlFor="dobDay">Dzień</label>
              <div className="field">
                <svg className="icon" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M7 4v3M17 4v3M4 9h16M6 6h12a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Z"
                    stroke="#6aaee6"
                    strokeWidth="1.6"
                  />
                </svg>
                <select
                  id="dobDay"
                  className="input"
                  style={{ paddingLeft: "42px" }}
                  {...register("dobDay", {
                    required: "Wybierz dzień urodzenia",
                  })}
                >
                  <option value="">Wybierz dzień</option>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                    <option key={day} value={String(day).padStart(2, "0")}>
                      {day}
                    </option>
                  ))}
                </select>
              </div>
              {errors.dobDay && (
                <div className="error" style={{ display: "block" }}>
                  {errors.dobDay.message}
                </div>
              )}
            </div>

            <div>
              <label htmlFor="dobMonth">Miesiąc</label>
              <div className="field">
                <svg className="icon" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M7 4v3M17 4v3M4 9h16M6 6h12a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Z"
                    stroke="#6aaee6"
                    strokeWidth="1.6"
                  />
                </svg>
                <select
                  id="dobMonth"
                  className="input"
                  style={{ paddingLeft: "42px" }}
                  {...register("dobMonth", {
                    required: "Wybierz miesiąc urodzenia",
                  })}
                >
                  <option value="">Wybierz miesiąc</option>
                  <option value="01">Styczeń</option>
                  <option value="02">Luty</option>
                  <option value="03">Marzec</option>
                  <option value="04">Kwiecień</option>
                  <option value="05">Maj</option>
                  <option value="06">Czerwiec</option>
                  <option value="07">Lipiec</option>
                  <option value="08">Sierpień</option>
                  <option value="09">Wrzesień</option>
                  <option value="10">Październik</option>
                  <option value="11">Listopad</option>
                  <option value="12">Grudzień</option>
                </select>
              </div>
              {errors.dobMonth && (
                <div className="error" style={{ display: "block" }}>
                  {errors.dobMonth.message}
                </div>
              )}
            </div>
{/* 
            <div>
              <label htmlFor="dobYear">Rok</label>
              <div className="field">
                <svg className="icon" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M7 4v3M17 4v3M4 9h16M6 6h12a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Z"
                    stroke="#6aaee6"
                    strokeWidth="1.6"
                  />
                </svg>
                <select
                  id="dobYear"
                  className="input"
                  style={{ paddingLeft: "42px" }}
                  {...register("dobYear", {
                    required: "Wybierz rok urodzenia",
                  })}
                >
                  <option value="">Wybierz rok</option>
                  {Array.from({ length: 105 }, (_, i) => new Date().getFullYear() - i).map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
              {errors.dobYear && (
                <div className="error" style={{ display: "block" }}>
                  {errors.dobYear.message}
                </div>
              )}
            </div> */}
          </div>

          <EADivider />

          <div>
            <h3 style={{ margin: "0 0 4px 0", fontSize: "14px", fontWeight: 600, color: "var(--muted)" }}>Zgody</h3>
          </div>

          {/* Checkboxy */}
          <label className="checkbox">
            <input
              type="checkbox"
              id="allowMailing"
              {...register("allowMailing", {
                required: "Newsletter jest wymagany",
              })}
            />
            <div>
              <b>Chcę otrzymywać newsletter</b>
              <small>(wymagane)</small>
            </div>
          </label>
          {errors.allowMailing && (
            <div className="error" style={{ display: "block", marginTop: "-8px" }}>
              {errors.allowMailing.message}
            </div>
          )}

          <label className="checkbox">
            <input
              type="checkbox"
              id="acceptStatute"
              {...register("acceptStatute", {
                required: "Musisz zaakceptować regulamin",
              })}
            />
            <div>
              <b>Akceptuję  <a href="https://panel.eaktywni.pl/img/1233/regulamin_karty_przyjaciol.pdf" target="_blank">Regulamin Programu „Klub przyjaciół Term Uniejów”</a></b>
            </div>
          </label>
          {errors.acceptStatute && (
            <div className="error" style={{ display: "block", marginTop: "-8px" }}>
              {errors.acceptStatute.message}
            </div>
          )}

          <label className="checkbox">
            <input
              type="checkbox"
              id="acceptRodo"
              {...register("acceptRodo", {
                required: "Zgoda na przetwarzanie danych jest wymagana",
              })}
            />
            <div>
              <b>Wyrażam zgodę na <a href="https://panel.eaktywni.pl/img/1199/Polityka_termyUniejow.pdf" target="_blank">przetwarzanie danych osobowych</a></b>
              <small>(wymagane)</small>
            </div>
          </label>

          <label className="checkbox">
            <input
              type="checkbox"
              id="acceptAduls"
              {...register("acceptAduls", {
                required: "Klub Przyjaciół Term Uniejów, jest przeznaczony dla osób powyżej 18 roku życia",
              })}
            />
            <div>
              <b>Oświadczam że ukończyłem 18 lat</b>
              <small>(wymagane)</small>
            </div>
          </label>



          {errors.acceptRodo && (
            <div className="error" style={{ display: "block", marginTop: "-8px" }}>
              {errors.acceptRodo.message}
            </div>
          )}


          {errors.acceptAduls && (
            <div className="error" style={{ display: "block", marginTop: "-8px" }}>
              {errors.acceptAduls.message}
            </div>
          )}


          {alertLogin && alertLogin.length > 0 && (
            <div className="error" style={{ display: "block", marginTop: "12px" }}>
              {alertLogin}
            </div>
          )}

          <div className="actions">
            <button className="btn" type="submit" disabled={loading}>
              {loading ? "REJESTRACJA..." : "DOŁĄCZAM"}
            </button>
            <button
              type="button"
              className="alt"
              style={{ background: "none", border: "none", cursor: "pointer", padding: 0 }}
              onClick={() => navigate(LOGIN_PAGE)}
            >
              Masz już konto? Zaloguj się
            </button>
          </div>
        </form>
      </section>
    </div>
  );
};

export default Registration;
